import { Action, ActionType } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { iif, Observable, of, from, OperatorFunction, throwError } from 'rxjs';
import { catchError, concatMap, delay, map, mergeMap, retryWhen, withLatestFrom } from 'rxjs/operators';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { PostPaymentOrderResponse } from '@dtp/membership-service-types';
import { Actions, actions } from '@redux/modules/loggedInActivationOrder';
import { TypedEpic } from './types';

const createOrderEpic: TypedEpic = (action$: Observable<Action<any>>, state$) => {
  const errorArray = [400, 422];

  return action$.pipe(
    ofType(Actions.CREATE_ORDER),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.createOrder.request>[]
    >,
    mergeMap(([action]) =>
      from(
        apiClient.post<PostPaymentOrderResponse>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].v2.productsLoginPayment,
          data: action.payload,
          withCredentials: true,
        }),
      ).pipe(
        map((response) => actions.createOrder.success(response.data)),
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) => {
              if (!errorArray.includes(error.status)) {
                return of(error);
              }
              return throwError(error);
            }),
            // Use concat map to keep the errors in order and make sure they
            // aren't executed in parallel
            concatMap((e, i) =>
              // Executes a conditional Observable depending on the result
              // of the first argument
              iif(
                () => i > 10,
                // If the condition is true we throw the error (the last error)
                throwError(e),
                // Otherwise we pipe this back into our stream and delay the retry
                of(e).pipe(delay(500)),
              ),
            ),
          ),
        ),
        catchError((error) => {
          if (error.status === 500)
            return of(
              actions.createOrder.failure({
                isValid: false,
                salesOrder: null,
                validationResults: [
                  {
                    field: 'CustomerProductsRequests[0].Profile.PhoneNumber',
                    message:
                      'Det har oppstått en ukjent feil på serveren, forsøk å laste inn siden på nytt. Hvis dette ikke fungerer, kontakt kundesenteret.',
                    isValid: false,
                    action: null,
                  },
                ],
                validationMap: {
                  'CustomerProductsRequests[0].Profile.PhoneNumber': {
                    field: 'CustomerProductsRequests[0].Profile.PhoneNumber',
                    message:
                      'Det har oppstått en ukjent feil på serveren, forsøk å laste inn siden på nytt. Hvis dette ikke fungerer, kontakt kundesenteret.',
                    isValid: false,
                    action: null,
                  },
                },
              }),
            );
          return of(actions.createOrder.failure(error.response?.data || error));
        }),
      ),
    ),
  );
};

export const epics: TypedEpic[] = [createOrderEpic];
